import { useSelector } from 'react-redux';

import { getLanguage } from 'api/utils/languageManagment';

import { selectCurrentUser } from 'store/user/selectors';

import { IGuide } from 'types/guide';
import { UserStore } from 'types/store/userStore';

import indoorWalkingChallenge from './images/indoor-walking-challenge.png';
import outdoorWalkingChallenge from './images/outdoor-walking-challenge.png';
import taiChiWalkingGuide from './images/tai-chi-walking.png';

const SUPPORTED_LOCALES = ['EN', 'ES', 'PT', 'FR', 'DE', 'IT'] as const;

type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

type WorkoutUrls = Record<SupportedLocale, string>;

interface FilterRule {
    field: keyof UserStore;
    values: string[];
    match?: 'include' | 'exclude';
}

interface FilteringRules {
    condition: 'AND' | 'OR';
    rules: FilterRule[];
}

interface WorkoutFile {
    name: string;
    preview: string;
    filteringRules: FilteringRules;
    urls: WorkoutUrls;
}

const TAI_CHI_LINK_IDS = [
    'go86max',
    'go77meh',
    'go20ice',
    'go93day',
    'go94man',
    'go97art',
    'go63chi',
    'go76ski',
    'go85luv',
    'go87rut',
    'go92eye',
    'go95owl',
    'go98elf',
    'go100one',
    'go102sun',
    'test_alek',
    'go103mal',
    'go104flo',
    'alek_test',
    'wellforge-wf-android',
    'alek_test_male',
    '106gpt',
];

const WORKOUT_FILES: WorkoutFile[] = [
    {
        name: 'printableWorkouts.workouts.taiChiWalkingGuide',
        preview: taiChiWalkingGuide,
        filteringRules: {
            condition: 'OR',
            rules: [
                { field: 'branch_name', values: ['mltaichi'] },
                { field: 'link_id', values: TAI_CHI_LINK_IDS },
            ],
        },
        urls: {
            EN: 'https://s3-eu-central-1.amazonaws.com/videoproduction-main.wikrgroup.com/!VIDEO/Walking%20App/Public/TaiChi_Walking/WF_Printable_Guide.pdf',
            ES: 'https://s3-eu-central-1.amazonaws.com/videoproduction-main.wikrgroup.com/!VIDEO/Walking%20App/Public/TaiChi_Walking/ES_WF_Printable_Guide.pdf',
            PT: 'https://s3-eu-central-1.amazonaws.com/videoproduction-main.wikrgroup.com/!VIDEO/Walking%20App/Public/TaiChi_Walking/PT_WF_Printable_Guide.pdf',
            FR: 'https://s3-eu-central-1.amazonaws.com/videoproduction-main.wikrgroup.com/!VIDEO/Walking%20App/Public/TaiChi_Walking/FR_WF_Printable_Guide.pdf',
            DE: 'https://s3-eu-central-1.amazonaws.com/videoproduction-main.wikrgroup.com/!VIDEO/Walking%20App/Public/TaiChi_Walking/DE_WF_Printable_Guide.pdf',
            IT: 'https://s3-eu-central-1.amazonaws.com/videoproduction-main.wikrgroup.com/!VIDEO/Walking%20App/Public/TaiChi_Walking/IT_WF_Printable_Guide.pdf',
        },
    },
    {
        name: 'printableWorkouts.workouts.indoorWalkingChallenge',
        preview: indoorWalkingChallenge,
        filteringRules: {
            condition: 'AND',
            rules: [
                { field: 'branch_name', values: ['indoor'] },
                { field: 'link_id', match: 'exclude', values: TAI_CHI_LINK_IDS },
            ],
        },
        urls: {
            EN: 'https://s3-eu-central-1.amazonaws.com/videoproduction-main.wikrgroup.com/!VIDEO/Walking%20App/Public/SWF_Indoor/WF_Printable_Indoor_Walking_Challenge.pdf',
            ES: 'https://s3-eu-central-1.amazonaws.com/videoproduction-main.wikrgroup.com/!VIDEO/Walking%20App/Public/SWF_Indoor/WF_Printable_Indoor_Walking_ES.pdf',
            PT: 'https://s3-eu-central-1.amazonaws.com/videoproduction-main.wikrgroup.com/!VIDEO/Walking%20App/Public/SWF_Indoor/WF_Printable_Indoor_Walking_PT.pdf',
            FR: 'https://s3-eu-central-1.amazonaws.com/videoproduction-main.wikrgroup.com/!VIDEO/Walking%20App/Public/SWF_Indoor/WF_Printable_Indoor_Walking_FR.pdf',
            DE: 'https://s3-eu-central-1.amazonaws.com/videoproduction-main.wikrgroup.com/!VIDEO/Walking%20App/Public/SWF_Indoor/WF_Printable_Indoor_Walking_DE.pdf',
            IT: 'https://s3-eu-central-1.amazonaws.com/videoproduction-main.wikrgroup.com/!VIDEO/Walking%20App/Public/SWF_Indoor/WF_Printable_Indoor_Walking_IT.pdf',
        },
    },
    {
        name: 'printableWorkouts.workouts.outdoorWalkingChallenge',
        preview: outdoorWalkingChallenge,
        filteringRules: {
            condition: 'OR',
            rules: [{ field: 'branch_name', values: ['wf_general'] }],
        },
        urls: {
            EN: 'https://s3-eu-central-1.amazonaws.com/videoproduction-main.wikrgroup.com/!VIDEO/Walking%20App/Public/SWF_Indoor/WF_Printable_Outdoor_Walking_Challenge.pdf',
            ES: 'https://s3-eu-central-1.amazonaws.com/videoproduction-main.wikrgroup.com/!VIDEO/Walking%20App/Public/SWF_Indoor/WF_Printable_Outdoor_Walking_ES.pdf',
            PT: 'https://s3-eu-central-1.amazonaws.com/videoproduction-main.wikrgroup.com/!VIDEO/Walking%20App/Public/SWF_Indoor/WF_Printable_Outdoor_Walking_PT.pdf',
            FR: 'https://s3-eu-central-1.amazonaws.com/videoproduction-main.wikrgroup.com/!VIDEO/Walking%20App/Public/SWF_Indoor/WF_Printable_Outdoor_Walking_FR.pdf',
            DE: 'https://s3-eu-central-1.amazonaws.com/videoproduction-main.wikrgroup.com/!VIDEO/Walking%20App/Public/SWF_Indoor/WF_Printable_Outdoor_Walking_DE.pdf',
            IT: 'https://s3-eu-central-1.amazonaws.com/videoproduction-main.wikrgroup.com/!VIDEO/Walking%20App/Public/SWF_Indoor/WF_Printable_Outdoor_Walking_IT.pdf',
        },
    },
];

const getLocale = (): SupportedLocale => {
    const lang = getLanguage().toUpperCase() as SupportedLocale;

    return SUPPORTED_LOCALES.includes(lang) ? lang : 'EN';
};

const matchesFilteringRules = ({ condition, rules }: FilteringRules, user: UserStore): boolean => {
    const results = rules.map((rule) => {
        const matches = rule.values.includes(user[rule.field] as string);

        return rule.match === 'exclude' ? !matches : matches;
    });

    return condition === 'AND' ? results.every(Boolean) : results.some(Boolean);
};

export const useGetBonusWorkouts = (): IGuide[] => {
    const user = useSelector(selectCurrentUser);
    const locale = getLocale();

    const matchedWorkouts = WORKOUT_FILES.filter((w) => matchesFilteringRules(w.filteringRules, user));
    const workoutsToRender = matchedWorkouts.length ? matchedWorkouts : WORKOUT_FILES;

    return workoutsToRender.map(({ name, preview, urls }) => ({
        name,
        preview,
        download_url: urls[locale] || urls.EN,
    }));
};
